import { Field, Formik, type FormikProps } from 'formik'
import React, { useRef } from 'react'

import { useDashboardFeatureFlags } from '@components/utils/DashboardFeatureFlags'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import { ANCHOR_IDS } from '@constants/anchorIds'
import {
  Banner,
  ButtonClean,
  Checkbox,
  Fieldset,
  HeadContent,
  RadioButton,
  Subsection,
} from '@fourthwall/components'
import type { RadioButtonProps } from '@fourthwall/components/dist/inputs/RadioButton/types'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import * as S from './Checkout.styled'
import type { CurrencyItem, FormValues } from './Checkout.types'
import { CurrenciesSelect } from './components/CurrenciesSelect/CurrenciesSelect'

export type CheckoutViewProps = {
  initialValues: FormValues
  currency: {
    list: CurrencyItem[]
    enabled: boolean
    yenEnabled: boolean
  }
  abandonedCheckout: {
    options: Pick<RadioButtonProps, 'checkedValue' | 'label'>[]
  }
  onSubmit: (values: FormValues) => void
}

export const CheckoutView = (props: CheckoutViewProps) => {
  const { dashboardFeatureFlags } = useDashboardFeatureFlags()
  const formRef = useRef<FormikProps<FormValues>>(null)

  const handleEmailFrequencyValueChange = (
    newValue: FormValues['abandonedCheckoutEmailFrequency'],
  ) => {
    formRef.current?.setFieldValue('cartAbandonedIncludePromoCode', newValue !== 'off')
  }

  return (
    <>
      <HeadContent title="Checkout &ndash; Settings" />
      <Formik<FormValues>
        initialValues={props.initialValues}
        enableReinitialize
        innerRef={formRef}
        onSubmit={props.onSubmit}
      >
        {({ values, dirty, isSubmitting, isValid, submitForm, resetForm, handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <Subsection title="Accept PayPal">
              <div>
                PayPal is one of the most trusted payment providers and offering it is known to
                increase sales, but it has higher payment processing rates at 3.49% + $0.49 per
                transactions (and an additional 1.5% for payments from outside the USA).
              </div>
              <div style={{ marginTop: 16 }}>
                <Fieldset>
                  <Fieldset.Row>
                    <Fieldset.Item>
                      <Field component={Checkbox} name="payPal" label="Accept PayPal at checkout" />
                    </Fieldset.Item>
                  </Fieldset.Row>
                </Fieldset>
              </div>
            </Subsection>
            <Subsection title="Accept buy now, pay later">
              <div>
                Buy now, pay later services (Afterpay, Klarna) allow your customers to pay over 4
                interest-free installments (on orders above $10). You receive your profit
                immediately, but these orders have a higher payment processing rate of 6% + $.30 per
                transaction (and an additional 1% for payments outside the US).{' '}
                <ButtonClean
                  label="Learn more"
                  href={routing.external.helpCenter.buyNowPayLater}
                  target="_blank"
                />
              </div>
              <div style={{ marginTop: 16 }}>
                <Fieldset>
                  <Fieldset.Row>
                    <Fieldset.Item>
                      <Field
                        component={Checkbox}
                        name="deferred"
                        label="Accept buy now, pay later"
                      />
                    </Fieldset.Item>
                  </Fieldset.Row>
                </Fieldset>
              </div>
            </Subsection>
            {dashboardFeatureFlags.multicurrencySettings.enabled && (
              <Subsection title="Local currencies">
                {!props.currency.enabled && (
                  <div style={{ marginBottom: 16 }}>
                    <Banner
                      title={
                        <>
                          Your custom theme does not support multi-currency. Please{' '}
                          <a href="mailto:support@fourthwall.com">contact our support</a> team or
                          your partner manager to enable multi-currency.
                        </>
                      }
                      appearance="warning"
                    />
                  </div>
                )}
                {props.currency.enabled && !props.currency.yenEnabled && (
                  <div style={{ marginBottom: 16 }}>
                    <Banner
                      title={
                        <>
                          Your custom theme does not support JPY. Please{' '}
                          <a href="mailto:support@fourthwall.com">contact our support</a> team or
                          your partner manager to enable JPY.
                        </>
                      }
                      appearance="warning"
                    />
                  </div>
                )}
                <S.SectionDescription>
                  When enabled, we’ll automatically convert prices to local currencies for
                  supporters. You’ll make the same profit per sale regardless of currency used.{' '}
                  <ButtonClean
                    label="Learn more"
                    href={routing.external.helpCenter.multicurrency}
                    target="_blank"
                  />
                </S.SectionDescription>
                <Field
                  name="enabledCurrencies"
                  component={CurrenciesSelect}
                  currencies={props.currency.list}
                  disabled={!props.currency.enabled}
                />
              </Subsection>
            )}
            <Subsection
              title="Abandoned checkout reminders"
              id={ANCHOR_IDS.ABANDONED_CHECKOUT_REMINDERS}
            >
              <S.SectionDescription>
                An email reminder sent to people who start checking out but don’t finish. On
                average, 10% of people who receive an abandoned cart reminder end up placing an
                order.{' '}
                <ButtonClean
                  label="Learn more"
                  href={routing.external.helpCenter.abandonedCheckoutEmails}
                  target="_blank"
                />
              </S.SectionDescription>
              <S.SectionHeader>Email frequency</S.SectionHeader>
              <Fieldset>
                {props.abandonedCheckout.options.map((option) => (
                  <Fieldset.Row>
                    <Fieldset.Item>
                      <Field
                        component={RadioButton}
                        name="abandonedCheckoutEmailFrequency"
                        onValueChange={handleEmailFrequencyValueChange}
                        {...option}
                      />
                    </Fieldset.Item>
                  </Fieldset.Row>
                ))}
              </Fieldset>
              <S.SectionHeader>Boost final email with 5% off</S.SectionHeader>
              <Fieldset>
                <Fieldset.Row>
                  <Fieldset.Item>
                    <Field
                      component={Checkbox}
                      label="Include 5% promo on whole order in the last email sequence"
                      name="cartAbandonedIncludePromoCode"
                      disabled={values.abandonedCheckoutEmailFrequency === 'off'}
                    />
                  </Fieldset.Item>
                </Fieldset.Row>
              </Fieldset>
            </Subsection>
            <ConfirmBar
              isOpen={dirty}
              isLoading={isSubmitting}
              onConfirm={submitForm}
              onCancel={resetForm}
            />
            <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
          </form>
        )}
      </Formik>
    </>
  )
}
